import type { MapData, MapAreaData, MapRect } from '../types/map';
import { getAreasAtTile, rectsArea, classifyZoneType, type ZoneType } from './areaUtils';
import { getNPCTemplate } from '../data/npcs';
import { QUEST_DEFINITIONS } from '../data/quests';
import { DEFAULT_KINGDOM, type KingdomId } from '../data/kingdoms';
import { getResourceNodePlacementsForMap, getResourceNodeType } from '../data/resourceNodes';
import type { SkillId } from '../types/skills';
import type { QuestObjective, QuestObjectiveView } from '../types/quest';

/**
 * Índice PURO del mapa para el World Map interactivo (tecla M).
 *
 * Deriva del `MapData` bundleado (áreas + placements de NPC) y de los catálogos
 * compartidos (templates de NPC con loot/reino, quests, items) TODO lo que el
 * mapa necesita mostrar sin tocar el server:
 *  - áreas / ciudades navegables (bbox + centro + tipo de zona),
 *  - NPCs neutrales (pins individuales) y hostiles (agregados por zona con conteo),
 *  - índice inverso item → NPCs que lo dropean (buscador "¿quién suelta X?"),
 *  - resolución del destino de cada objetivo de quest (kill/talk/reach/collect…).
 *
 * Es pura y sin estado de Phaser (testeable). Se **memoiza por `map.id`** igual
 * que `bakeMapTexture`, porque un `MapData` grande (map1 ~21MB) es caro de
 * recorrer y no cambia en runtime. Ver docs/WORLD_MAP.md.
 *
 * Convención: un NPC es "hostil" si su template es `attackable` (mismo criterio
 * que el minimapa, ver docs/MINIMAP.md). El resto son neutrales/interactivos.
 */

/** Rol de un NPC neutral, para el ícono del pin y el filtro. */
export type NpcRole = 'merchant' | 'spellVendor' | 'questGiver' | 'npc';

/** Un placement concreto de NPC en el mapa (posición de spawn estático). */
export interface WorldMapNpc {
  templateId: string;
  name: string;
  x: number;
  y: number;
  hostile: boolean;
  isBoss: boolean;
  kingdom: KingdomId;
  interact: boolean;
  /** Sólo relevante para neutrales (los hostiles van agregados por zona). */
  role: NpcRole;
}

/** Conteo de hostiles de un mismo template que respawnean en una zona. */
export interface HostileCount {
  templateId: string;
  name: string;
  isBoss: boolean;
  count: number;
}

/** NPCs asociados a un área: hostiles agregados + neutrales individuales. */
export interface WorldMapAreaNpcs {
  hostiles: HostileCount[];
  hostileTotal: number;
  neutrals: WorldMapNpc[];
}

/** Un yacimiento (nodo de recurso) en su posición fija del mapa. */
export interface WorldMapResourceNode {
  typeId: string;
  x: number;
  y: number;
  name: string;
  /** 'skill_mining' (pico) o 'skill_woodcutting' (hacha). */
  skill: SkillId;
  toolType: string;
  tier: string;
  requiredLevel: number;
  /** Item que entrega cada extracción (id). */
  producesItemId: string;
}

/** Un área/ciudad lista para navegar y dibujar. */
export interface WorldMapAreaEntry {
  id: string;
  name: string; // '' si el área no tiene nombre (no se lista en el dock)
  rects: MapRect[];
  bbox: { minX: number; minY: number; maxX: number; maxY: number };
  center: { x: number; y: number };
  tileCount: number;
  zoneType: ZoneType;
  safe: boolean;
  factionId?: string;
  /** true si es ciudad: segura y con nombre. */
  isCity: boolean;
}

export interface WorldMapIndex {
  mapId: string;
  /** Todas las áreas con nombre (ciudades + zonas). */
  areas: WorldMapAreaEntry[];
  cities: WorldMapAreaEntry[];
  /** Áreas con nombre no-ciudad (danger/neutral/especial). */
  zones: WorldMapAreaEntry[];
  neutrals: WorldMapNpc[];
  hostiles: WorldMapNpc[];
  /** Yacimientos (minería + leñería) en sus posiciones fijas. */
  resourceNodes: WorldMapResourceNode[];
  areaNpcs: Map<string, WorldMapAreaNpcs>;
  /** itemId → templateIds de NPCs que lo dropean. */
  dropIndex: Map<string, string[]>;
  npcsByTemplate: Map<string, WorldMapNpc[]>;
  npcsByKingdom: Map<KingdomId, WorldMapNpc[]>;
}

// Cache por id de mapa (los MapData no mutan en runtime).
const CACHE = new Map<string, WorldMapIndex>();

/** Set de templateIds que ofrecen o reciben alguna quest (para el rol questGiver). */
function questNpcIds(): Set<string> {
  const set = new Set<string>();
  for (const def of Object.values(QUEST_DEFINITIONS)) {
    set.add(def.giverNpcId);
    if (def.turnInNpcId) set.add(def.turnInNpcId);
  }
  return set;
}

/** Rol de un NPC neutral según su template (merchant/spellVendor/questGiver/npc). */
function roleOf(templateId: string, questGivers: Set<string>): NpcRole {
  const tpl = getNPCTemplate(templateId);
  if (!tpl) return 'npc';
  if (tpl.spellVendorId) return 'spellVendor';
  if (tpl.merchantId) return 'merchant';
  if (questGivers.has(templateId)) return 'questGiver';
  return 'npc';
}

function bboxOf(rects: MapRect[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const r of rects) {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.w - 1);
    maxY = Math.max(maxY, r.y + r.h - 1);
  }
  if (!isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

/** Construye (o reusa cacheado) el índice del mapa. */
export function buildWorldMapIndex(map: MapData): WorldMapIndex {
  const cached = CACHE.get(map.id);
  if (cached) return cached;

  const questGivers = questNpcIds();
  const areasData: MapAreaData[] = map.areas ?? [];

  // --- Áreas -------------------------------------------------------------
  const areas: WorldMapAreaEntry[] = [];
  for (const a of areasData) {
    if (!a.rects || a.rects.length === 0) continue;
    const name = a.name ?? '';
    if (!name) continue; // sólo áreas con nombre se navegan/listan
    const bbox = bboxOf(a.rects);
    const zoneType = classifyZoneType([a]);
    const safe = a.safe === true;
    areas.push({
      id: a.id,
      name,
      rects: a.rects,
      bbox,
      center: {
        x: Math.round((bbox.minX + bbox.maxX) / 2),
        y: Math.round((bbox.minY + bbox.maxY) / 2),
      },
      tileCount: rectsArea(a.rects),
      zoneType,
      safe,
      factionId: a.factionId,
      isCity: safe && !!name,
    });
  }
  const cities = areas.filter((a) => a.isCity);
  const zones = areas.filter((a) => !a.isCity);

  // --- NPCs (placements) -------------------------------------------------
  const neutrals: WorldMapNpc[] = [];
  const hostiles: WorldMapNpc[] = [];
  const npcsByTemplate = new Map<string, WorldMapNpc[]>();
  const npcsByKingdom = new Map<KingdomId, WorldMapNpc[]>();

  for (const placement of map.npcs ?? []) {
    const tpl = getNPCTemplate(placement.templateId);
    if (!tpl) continue;
    const hostile = tpl.attackable === true;
    const kingdom = tpl.kingdom ?? DEFAULT_KINGDOM;
    const npc: WorldMapNpc = {
      templateId: placement.templateId,
      name: tpl.name,
      x: placement.x,
      y: placement.y,
      hostile,
      isBoss: tpl.isBoss === true,
      kingdom,
      interact: (tpl.interact ?? 0) === 1,
      role: hostile ? 'npc' : roleOf(placement.templateId, questGivers),
    };
    (hostile ? hostiles : neutrals).push(npc);

    const byTpl = npcsByTemplate.get(npc.templateId);
    if (byTpl) byTpl.push(npc);
    else npcsByTemplate.set(npc.templateId, [npc]);

    const byKing = npcsByKingdom.get(kingdom);
    if (byKing) byKing.push(npc);
    else npcsByKingdom.set(kingdom, [npc]);
  }

  // --- NPCs por área -----------------------------------------------------
  // Un neutral pertenece al área que lo contiene. Un hostil "respawnea" en un
  // área si cae dentro de ella O si su placement apunta a esa área por
  // respawnAreaId (mob colocado en otro lado que reaparece dentro del área).
  const areaNpcs = new Map<string, WorldMapAreaNpcs>();
  const ensureEntry = (id: string): WorldMapAreaNpcs => {
    let e = areaNpcs.get(id);
    if (!e) {
      e = { hostiles: [], hostileTotal: 0, neutrals: [] };
      areaNpcs.set(id, e);
    }
    return e;
  };

  for (const npc of neutrals) {
    for (const a of getAreasAtTile(areasData, npc.x, npc.y)) {
      ensureEntry(a.id).neutrals.push(npc);
    }
  }

  // Hostiles: acumular conteo por (área, template). Evitar doble conteo si un
  // placement cae en el área Y además tiene respawnAreaId igual.
  const hostileAccum = new Map<string, Map<string, HostileCount>>(); // areaId → templateId → count
  const bumpHostile = (areaId: string, npc: WorldMapNpc): void => {
    let perTpl = hostileAccum.get(areaId);
    if (!perTpl) {
      perTpl = new Map();
      hostileAccum.set(areaId, perTpl);
    }
    const existing = perTpl.get(npc.templateId);
    if (existing) existing.count += 1;
    else perTpl.set(npc.templateId, { templateId: npc.templateId, name: npc.name, isBoss: npc.isBoss, count: 1 });
  };

  for (let i = 0; i < (map.npcs ?? []).length; i++) {
    const placement = map.npcs![i];
    const tpl = getNPCTemplate(placement.templateId);
    if (!tpl || tpl.attackable !== true) continue;
    const npc: WorldMapNpc = {
      templateId: placement.templateId,
      name: tpl.name,
      x: placement.x,
      y: placement.y,
      hostile: true,
      isBoss: tpl.isBoss === true,
      kingdom: tpl.kingdom ?? DEFAULT_KINGDOM,
      interact: false,
      role: 'npc',
    };
    const targetAreas = new Set<string>();
    if (placement.respawnAreaId) targetAreas.add(placement.respawnAreaId);
    else for (const a of getAreasAtTile(areasData, npc.x, npc.y)) targetAreas.add(a.id);
    for (const areaId of targetAreas) bumpHostile(areaId, npc);
  }

  for (const [areaId, perTpl] of hostileAccum) {
    const entry = ensureEntry(areaId);
    entry.hostiles = Array.from(perTpl.values()).sort((a, b) => b.count - a.count);
    entry.hostileTotal = entry.hostiles.reduce((s, h) => s + h.count, 0);
  }

  // --- Índice inverso de drops ------------------------------------------
  // itemId → templateIds que lo listan en su lootTable. Sólo templates con
  // placement en el mapa (los que el jugador puede encontrar acá).
  const placedTemplates = new Set<string>(npcsByTemplate.keys());
  const dropIndex = new Map<string, string[]>();
  for (const templateId of placedTemplates) {
    const tpl = getNPCTemplate(templateId);
    if (!tpl?.lootTable) continue;
    for (const entry of tpl.lootTable) {
      const list = dropIndex.get(entry.itemId);
      if (list) {
        if (!list.includes(templateId)) list.push(templateId);
      } else {
        dropIndex.set(entry.itemId, [templateId]);
      }
    }
  }

  // --- Yacimientos (nodos de recurso) -----------------------------------
  // Posiciones fijas desde shared (minería + leñería). Se deduplican por celda
  // porque los placements de un mismo tipo pueden apilarse (colección de debug).
  const resourceNodes: WorldMapResourceNode[] = [];
  for (const p of getResourceNodePlacementsForMap(map.id)) {
    const type = getResourceNodeType(p.typeId);
    if (!type) continue;
    resourceNodes.push({
      typeId: p.typeId,
      x: p.x,
      y: p.y,
      name: type.name,
      skill: type.skill,
      toolType: type.toolType,
      tier: type.tier,
      requiredLevel: type.requiredLevel,
      producesItemId: type.producesItemId,
    });
  }

  const index: WorldMapIndex = {
    mapId: map.id,
    areas,
    cities,
    zones,
    neutrals,
    hostiles,
    resourceNodes,
    areaNpcs,
    dropIndex,
    npcsByTemplate,
    npcsByKingdom,
  };
  CACHE.set(map.id, index);
  return index;
}

// ============================================================================
// RESOLUCIÓN DE OBJETIVOS DE QUEST → posiciones en el mapa
// ============================================================================

export interface QuestTarget {
  x: number;
  y: number;
  /** Etiqueta corta del destino (nombre del NPC / área). */
  label: string;
}

/**
 * Resuelve los tiles candidatos donde cumplir un objetivo de quest, usando los
 * placements/áreas del índice. Devuelve [] si no hay ubicación conocida en este
 * mapa (ej. un NPC que no está colocado acá).
 */
export function resolveObjectiveTargets(
  obj: QuestObjective,
  index: WorldMapIndex
): QuestTarget[] {
  switch (obj.type) {
    case 'kill':
    case 'talk': {
      const list = index.npcsByTemplate.get(obj.npcId) ?? [];
      return list.map((n) => ({ x: n.x, y: n.y, label: n.name }));
    }
    case 'kill_kingdom': {
      const list = index.npcsByKingdom.get(obj.kingdom) ?? [];
      return list.filter((n) => n.hostile).map((n) => ({ x: n.x, y: n.y, label: n.name }));
    }
    case 'reach': {
      const area = index.areas.find((a) => a.id === obj.areaId);
      return area ? [{ x: area.center.x, y: area.center.y, label: area.name }] : [];
    }
    case 'collect': {
      const templateIds = index.dropIndex.get(obj.itemId) ?? [];
      const targets: QuestTarget[] = [];
      for (const tplId of templateIds) {
        for (const n of index.npcsByTemplate.get(tplId) ?? []) {
          targets.push({ x: n.x, y: n.y, label: n.name });
        }
      }
      return targets;
    }
  }
}

/**
 * Igual que `resolveObjectiveTargets` pero desde el `QuestObjectiveView` que
 * viaja al cliente (tiene `type` + `npcId`/`itemId`/`areaId`/`kingdom`). Útil
 * para trazar la ruta a la quest trackeada en el World Map.
 */
export function resolveObjectiveViewTargets(
  view: QuestObjectiveView,
  index: WorldMapIndex
): QuestTarget[] {
  switch (view.type) {
    case 'kill':
    case 'talk':
      return view.npcId
        ? (index.npcsByTemplate.get(view.npcId) ?? []).map((n) => ({ x: n.x, y: n.y, label: n.name }))
        : [];
    case 'kill_kingdom':
      return view.kingdom
        ? (index.npcsByKingdom.get(view.kingdom) ?? [])
            .filter((n) => n.hostile)
            .map((n) => ({ x: n.x, y: n.y, label: n.name }))
        : [];
    case 'reach': {
      const area = view.areaId ? index.areas.find((a) => a.id === view.areaId) : undefined;
      return area ? [{ x: area.center.x, y: area.center.y, label: area.name }] : [];
    }
    case 'collect': {
      if (!view.itemId) return [];
      const targets: QuestTarget[] = [];
      for (const tplId of index.dropIndex.get(view.itemId) ?? []) {
        for (const n of index.npcsByTemplate.get(tplId) ?? []) {
          targets.push({ x: n.x, y: n.y, label: n.name });
        }
      }
      return targets;
    }
  }
}

/** El target más cercano (Chebyshev) a un origen, o null si la lista está vacía. */
export function nearestTarget(
  targets: QuestTarget[],
  from: { x: number; y: number }
): QuestTarget | null {
  let best: QuestTarget | null = null;
  let bestD = Infinity;
  for (const t of targets) {
    const d = Math.max(Math.abs(t.x - from.x), Math.abs(t.y - from.y));
    if (d < bestD) {
      bestD = d;
      best = t;
    }
  }
  return best;
}
